import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

import { Separator } from './ui/separator';
import {
  Send,
  Save,
  X,
  Paperclip,
  ChevronDown,
  ChevronUp,
  Trash2,
  UserPlus,
  File as FileIcon,
  Image as ImageIcon,
} from 'lucide-react';
import { cn } from '../lib/utils';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const splitAddresses = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value).split(',').map(v => v.trim()).filter(Boolean);
};

const normalizeAddress = (value) => {
  const match = String(value || '').match(/<([^>]+)>/);
  return (match ? match[1] : String(value || '')).trim().toLowerCase();
};

const uniqueAddresses = (items, excluded = new Set()) => {
  const seen = new Set();
  return items.filter(item => {
    const key = normalizeAddress(item);
    if (!key || seen.has(key) || excluded.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const ComposeModal = ({
  isOpen,
  onClose,
  onSend,
  onSaveDraft,
  initialData,
  userEmail,
  userName,
  accounts = [],
  activeAccount,
}) => {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [contactSuggestions, setContactSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [showAddContact, setShowAddContact] = useState(false);
  const [sendAs, setSendAs] = useState('');
  const fileInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const bodyEditorRef = useRef(null);
  const safeAccounts = useMemo(() => Array.isArray(accounts) ? accounts : [], [accounts]);
  const accountEmailKey = safeAccounts.map(a => a.email).filter(Boolean).join('|');

  const sendAsOptions = safeAccounts.length > 0 ? safeAccounts : (userEmail ? [{ id: 'primary', email: userEmail, label: userName || userEmail }] : []);

  useEffect(() => {
    if (!isOpen) return;
    const opts = safeAccounts.length > 0 ? safeAccounts : (userEmail ? [{ id: 'primary', email: userEmail, label: userName || userEmail }] : []);
    if (opts.length > 0 && !sendAs) {
      const defaultEmail = activeAccount || opts.find(a => a.is_default)?.email || opts[0]?.email;
      setSendAs(defaultEmail || '');
    }
  }, [isOpen, accountEmailKey, userEmail, userName, activeAccount, sendAs, safeAccounts]);

  useEffect(() => {
    if (initialData) {
      const replyFrom = initialData.replyFromAccount || activeAccount;
      if (initialData.replyTo) {
        const em = initialData.replyTo;
        const ownAddresses = new Set(
          [userEmail, activeAccount, replyFrom, ...safeAccounts.map(a => a.email)]
            .map(normalizeAddress)
            .filter(Boolean)
        );
        const replyToSender = uniqueAddresses([em.from_email], ownAddresses);
        const originalTo = uniqueAddresses(splitAddresses(em.to || em.to_email), ownAddresses);
        const originalCc = uniqueAddresses(splitAddresses(em.cc), ownAddresses);
        const dateStr = new Date(em.created_at).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' });
        const senderStr = em.from_name ? `${em.from_name} &lt;${em.from_email}&gt;` : em.from_email;
        const quote = `<br><br><div style="border-left:2px solid #ccc;margin:0 0 0 0.5ex;padding-left:1ex;color:#555">
<div style="margin-bottom:6px;font-size:12px">Le ${dateStr}, ${senderStr} a écrit :</div>
${em.body || em.body_preview || ''}
</div>`;
        setTo((initialData.replyAll ? uniqueAddresses([...replyToSender, ...originalTo], ownAddresses) : replyToSender).join(', '));
        setCc(initialData.replyAll ? originalCc.join(', ') : '');
        setBcc('');
        setShowCcBcc(initialData.replyAll && originalCc.length > 0);
        setSubject(em.subject?.startsWith('Re:') ? em.subject : `Re: ${em.subject || ''}`);
        setBody(quote);
        if (replyFrom) setSendAs(replyFrom);
      } else if (initialData.forward) {
        const em = initialData.forward;
        const dateStr = new Date(em.created_at).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' });
        const toStr = Array.isArray(em.to) ? em.to.join(', ') : (em.to_email || '');
        const quote = `<br><br><div style="border-left:2px solid #ccc;margin:0 0 0 0.5ex;padding-left:1ex;color:#555">
<div style="margin-bottom:6px;font-size:12px;line-height:1.8">
  <b>De :</b> ${em.from_name ? `${em.from_name} &lt;${em.from_email}&gt;` : em.from_email}<br>
  <b>Date :</b> ${dateStr}<br>
  <b>Objet :</b> ${em.subject || ''}<br>
  ${toStr ? `<b>À :</b> ${toStr}` : ''}
</div>
${em.body || em.body_preview || ''}
</div>`;
        setTo('');
        setCc('');
        setBcc('');
        setShowCcBcc(false);
        setSubject(`Fwd: ${em.subject || ''}`);
        setBody(quote);
        if (replyFrom) setSendAs(replyFrom);
      }
    } else {
      setTo('');
      setCc('');
      setBcc('');
      setSubject('');
      setBody('');
      setAttachments([]);
    }
  }, [initialData, isOpen, activeAccount, accountEmailKey, userEmail, safeAccounts]);

  // Sync contentEditable innerHTML quand body change programmatiquement (forward/reply/reset)
  const prevBody = useRef('');
  useEffect(() => {
    if (bodyEditorRef.current && body !== prevBody.current) {
      bodyEditorRef.current.innerHTML = body;
      prevBody.current = body;
    }
  }, [body]);

  const handleBodyInput = useCallback((e) => {
    const html = e.currentTarget.innerHTML;
    prevBody.current = html;
    setBody(html);
  }, []);

  const fetchContacts = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setContactSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/contacts`, {
        params: { q: query }
      });
      const contacts = Array.isArray(res.data) ? res.data : [];
      if (contacts.length > 0) {
        setContactSuggestions(contacts);
        setShowSuggestions(true);
      } else {
        setContactSuggestions([]);
        setShowSuggestions(false);
      }
    } catch {
      setShowSuggestions(false);
    }
  }, []);

  const handleToChange = (e) => {
    const val = e.target.value;
    setTo(val);
    const lastEmail = val.split(',').pop().trim();
    fetchContacts(lastEmail);
    setActiveField('to');
    if (lastEmail && lastEmail.includes('@')) {
      setShowAddContact(true);
    } else {
      setShowAddContact(false);
    }
  };

  const selectContact = (contact) => {
    const parts = to.split(',').map(s => s.trim()).filter(Boolean);
    parts.pop();
    parts.push(contact.email);
    setTo(parts.join(', ') + (parts.length > 0 ? ', ' : ''));
    setShowSuggestions(false);
  };

  const addToContacts = async () => {
    const emails = to.split(',').map(s => s.trim()).filter(Boolean);
    const lastEmail = emails[emails.length - 1];
    if (!lastEmail || !lastEmail.includes('@')) return;
    try {
      await axios.post(`${API}/contacts`, {
        email: lastEmail,
        name: lastEmail.split('@')[0]
      });
      setShowAddContact(false);
      alert('Contact ajouté !');
    } catch (err) {
      console.error('Erreur ajout contact:', err);
      alert('Erreur lors de l\'ajout du contact');
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 25 * 1024 * 1024; // 25MB

    for (const file of files) {
      if (file.size > maxSize) {
        alert(`Le fichier "${file.name}" dépasse la taille maximale de 25MB`);
        continue;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        setAttachments(prev => [...prev, {
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          data: base64,
          size: file.size,
        }]);
      };
      reader.readAsDataURL(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (idx) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / 1048576).toFixed(1)} Mo`;
  };

  const buildPayload = () => ({
    to: to.split(',').map(e => e.trim()).filter(Boolean),
    cc: cc ? cc.split(',').map(e => e.trim()).filter(Boolean) : [],
    bcc: bcc ? bcc.split(',').map(e => e.trim()).filter(Boolean) : [],
    subject,
    body: body || '<p></p>',
    is_html: true,
    attachments: attachments.map(a => ({
      filename: a.filename,
      content_type: a.content_type,
      data: a.data,
    })),
    in_reply_to: initialData?.replyTo?.thread_id || initialData?.replyTo?.reply_to || undefined,
    references: initialData?.replyTo
      ? [initialData.replyTo.reply_to, initialData.replyTo.thread_id].filter(Boolean)
      : [],
  });

  const handleSend = async () => {
    if (!to.trim()) return;
    setIsSending(true);
    try {
      await onSend(buildPayload(), sendAs || null);
      handleClose();
    } catch (error) {
      console.error('Send error:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      await onSaveDraft(buildPayload(), sendAs || null);
      handleClose();
    } catch (error) {
      console.error('Draft error:', error);
    }
  };

  const handleClose = () => {
    setTo('');
    setCc('');
    setBcc('');
    setSubject('');
    setBody('');
    setSendAs('');
    setShowCcBcc(false);
    setIsMinimized(false);
    setAttachments([]);
    setShowSuggestions(false);
    setShowAddContact(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className={cn(
          "compose-modal p-0 gap-0",
          "w-[95vw] max-w-2xl",
          "max-h-[90vh] max-h-[90dvh]",
          "flex flex-col",
          isMinimized && "h-auto"
        )}
        data-testid="compose-modal"
      >
        {/* Header */}
        <DialogHeader className="flex flex-row items-center justify-between p-3 md:p-4 border-b border-border flex-shrink-0">
          <div>
            <DialogTitle className="text-base md:text-lg font-semibold">
            {initialData?.replyTo ? 'Répondre' : initialData?.forward ? 'Transférer' : 'Nouveau message'}
          </DialogTitle>
            <DialogDescription className="sr-only">Composez et envoyez un nouveau message</DialogDescription>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="w-8 h-8 hidden md:flex"
              onClick={() => setIsMinimized(!isMinimized)}
              data-testid="minimize-btn"
            >
              {isMinimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="w-8 h-8 bg-primary/20 hover:bg-primary/40 text-primary rounded-full"
              onClick={handleClose}
              data-testid="close-compose-btn"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        {!isMinimized && (
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Envoyer en tant que : boutons jaunes distincts (≤3) ou menu (>3) */}
            {sendAsOptions.length > 1 && (
              <div className="px-3 md:px-4 pt-2 flex flex-wrap items-center gap-2">
                <Label className="text-sm text-muted-foreground whitespace-nowrap">De</Label>
                {sendAsOptions.length <= 3 ? (
                  sendAsOptions.map((acc) => (
                    <Button
                      key={acc.id}
                      type="button"
                      size="sm"
                      variant={sendAs === acc.email ? "default" : "outline"}
                      className={sendAs === acc.email ? "btn-gold-glow" : "border-amber-500/50 text-amber-600 dark:text-amber-400 hover:bg-amber-500/15 hover:border-amber-500/70"}
                      onClick={() => setSendAs(acc.email)}
                      data-testid={`send-as-${acc.id}`}
                    >
                      {acc.label || acc.email}
                    </Button>
                  ))
                ) : (
                  <select
                    value={sendAs}
                    onChange={(e) => setSendAs(e.target.value)}
                    className="flex-1 min-w-0 h-9 rounded-md border border-amber-500/50 bg-background px-3 text-sm text-foreground"
                    data-testid="send-as-select"
                  >
                    {sendAsOptions.map((acc) => (
                      <option key={acc.id} value={acc.email}>
                        {acc.label || acc.email}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
            {/* Recipients */}
            <div className="p-3 md:p-4 space-y-3 flex-shrink-0">
              <div className="relative">
                <div className="flex items-center gap-2 md:gap-3">
                  <Label className="w-10 md:w-12 text-sm text-muted-foreground">À</Label>
                  <Input
                    placeholder="destinataire@exemple.com"
                    value={to}
                    onChange={handleToChange}
                    onFocus={() => setActiveField('to')}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    className="flex-1 h-10"
                    data-testid="to-input"
                  />
                  {showAddContact && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-8 h-8 text-primary"
                      onClick={addToContacts}
                      title="Ajouter aux contacts"
                    >
                      <UserPlus className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowCcBcc(!showCcBcc)}
                    className="text-xs text-muted-foreground px-2"
                    data-testid="toggle-cc-bcc"
                  >
                    {showCcBcc ? 'Masquer' : 'Cc/Cci'}
                  </Button>
                </div>
                {showSuggestions && contactSuggestions.length > 0 && activeField === 'to' && (
                  <div ref={suggestionsRef} className="absolute left-12 right-0 top-full z-50 bg-background border border-border rounded-md shadow-lg mt-1 max-h-40 overflow-auto">
                    {contactSuggestions.map((c, i) => (
                      <button
                        key={i}
                        className="w-full text-left px-3 py-2 hover:bg-accent text-sm flex items-center gap-2"
                        onMouseDown={(e) => { e.preventDefault(); selectContact(c); }}
                      >
                        <span className="font-medium">{c.name || c.email}</span>
                        {c.name && <span className="text-muted-foreground text-xs">&lt;{c.email}&gt;</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {showCcBcc && (
                <>
                  <div className="flex items-center gap-2 md:gap-3">
                    <Label className="w-10 md:w-12 text-sm text-muted-foreground">Cc</Label>
                    <Input
                      placeholder="copie@exemple.com"
                      value={cc}
                      onChange={(e) => setCc(e.target.value)}
                      className="flex-1 h-10"
                      data-testid="cc-input"
                    />
                  </div>
                  <div className="flex items-center gap-2 md:gap-3">
                    <Label className="w-10 md:w-12 text-sm text-muted-foreground">Cci</Label>
                    <Input
                      placeholder="copie.cachee@exemple.com"
                      value={bcc}
                      onChange={(e) => setBcc(e.target.value)}
                      className="flex-1 h-10"
                      data-testid="bcc-input"
                    />
                  </div>
                </>
              )}
              
              <div className="flex items-center gap-2 md:gap-3">
                <Label className="w-10 md:w-12 text-sm text-muted-foreground">Objet</Label>
                <Input
                  placeholder="Objet du message"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="flex-1 h-10"
                  data-testid="subject-input"
                />
              </div>
            </div>

            <Separator className="flex-shrink-0" />

            {/* Body - Scrollable, contentEditable pour rendre le HTML des forwards/replies */}
            <div className="flex-1 overflow-auto min-h-0">
              <div
                ref={bodyEditorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleBodyInput}
                className="min-h-[150px] md:min-h-[200px] h-full border-0 rounded-none focus:outline-none p-3 md:p-4 text-sm"
                style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}
                data-testid="body-input"
                data-placeholder="Rédigez votre message..."
              />
            </div>

            {/* Attachments */}
            {attachments.length > 0 && (
              <div className="px-3 md:px-4 py-2 border-t border-border flex-shrink-0">
                <div className="flex flex-wrap gap-2">
                  {attachments.map((att, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-accent/50 rounded-lg px-3 py-1.5 text-sm">
                      {att.content_type.startsWith('image/') ? (
                        <ImageIcon className="w-4 h-4 text-primary" />
                      ) : (
                        <FileIcon className="w-4 h-4 text-primary" />
                      )}
                      <span className="max-w-[120px] truncate">{att.filename}</span>
                      <span className="text-muted-foreground text-xs">({formatSize(att.size)})</span>
                      <button onClick={() => removeAttachment(idx)} className="text-muted-foreground hover:text-destructive ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between p-3 md:p-4 border-t border-border flex-shrink-0 gap-2">
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleSend}
                  disabled={!to.trim() || isSending}
                  className="btn-gold-glow h-10 px-5"
                  data-testid="send-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {isSending ? 'Envoi en cours...' : 'Envoyer'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleSaveDraft}
                  className="h-10 px-3"
                  data-testid="save-draft-btn"
                >
                  <Save className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Brouillon</span>
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                  accept="*/*"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-10 h-10"
                  onClick={() => fileInputRef.current?.click()}
                  title="Joindre un fichier"
                >
                  <Paperclip className="w-4 h-4" />
                </Button>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="text-muted-foreground w-10 h-10"
                data-testid="discard-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
